import type {
  IAthleteHighlight,
  IBasicVideo,
  IClip,
  IEvent,
  IEventAthleteMetricStats,
  IEventAthleteStats,
  IEventAthleteStatsV3,
  IEventGameStats,
  IEventScoreStats,
  IGameHighlight,
  IGameStats,
  IMbeEvent,
  IPlaylist,
  IResponseClip,
  IResponseEvent,
  IResponseEventStatsRosters,
  IResponseEventStatsRostersV3,
  IResponseEventStatsScores,
  IResponseEventStatsTeams,
  IResponseFavoriteVideo,
  IResponseHighlight,
  IResponseMBEEvent,
  IResponsePlayerHighlight,
  IResponsePlaylist,
  IResponseSharedExternalEvent,
  IResponseTag,
  ISharedExternalEvent,
  IStatsRepresentation,
  ITag,
  TEventGameType,
  TSportType,
} from "~/types";
import { ALLOWED_SPORT_TYPES_FOR_PDF_EXPORT, EVENT_STATUS, EVENT_TYPES_WITH_HOME_AWAY_TEAM, PLAY_BASED_GAMES, VIDEO_TYPE } from "~/constants";
import { formatPlaylist } from "~/videos/helpers/format-playlist";
import getStatValue, { getStatValueV3, type IStatValueOptions, type ItemStatValue } from "~/videos/helpers/get-stat-value";
import { VIDEO_SOURCE_TYPE } from "~/videos/types/video";

import {
  formatClipVideoAngles,
  formatEventVideoAngles,
  formatHighlightVideoAngles,
  formatInlineHighlightVideoAngles,
  formatMBEEventVideoAngles,
  formatPlayerHighlightVideoAngles,
} from "./format-video-angles";

// Helper function to get the team side based on the location
export function getTeamSide<T extends string>({ team, home = "home" as T, away = "away" as T, isUS }: { team: T; home?: T; away?: T; isUS?: boolean }): T {
  if (!isUS) return team;
  const oppositeTeam = { [home]: away, [away]: home } as const;
  return oppositeTeam[team as keyof typeof oppositeTeam] as T;
}

// Helper function to get the teams entities based on the location
function getTeamEntities<T>(home: T, away: T, isUS?: boolean): { team_home: T; team_away: T } {
  return {
    team_home: isUS ? away : home,
    team_away: isUS ? home : away,
  };
}

// Helper function to get the edit lineup permission based on the permissions
const getEditLineupPermission = (permissions: IResponseEvent["meta"]["permissions"]): IEvent["permissions"]["canEditLineup"] => {
  return {
    home: Boolean(permissions?.includes("lineup:write") || permissions?.includes("lineup:write:home")),
    away: Boolean(permissions?.includes("lineup:write") || permissions?.includes("lineup:write:away")),
  };
};

const getEditEventDetails = (gameInfo: IEvent["game_info"]) => {
  return {
    canonical: Boolean(isFeatureEnabled("enable_event_edit_details_canonical") && gameInfo.team1_is_official && gameInfo.team2_is_official),
    noncanonical: Boolean(isFeatureEnabled("enable_event_edit_details_non_canonical") && (!gameInfo.team1_is_official || !gameInfo.team2_is_official)),
  };
};

// Helper function to determine if event should use v2 playlists API
const shouldUseV2PlaylistsAPI = (event: IResponseEvent): boolean => {
  // Check if it's a play-based event
  const isPlayBased = PLAY_BASED_GAMES.includes(event.attributes.gameInfo.name);
  if (!isPlayBased || !event.attributes.videosList?.length) {
    return false;
  }

  // Check if the event is clipped (has all clipped video URLs)
  const hasClippedVideo = event.attributes.videosList.every((video) => video.videoType === VIDEO_SOURCE_TYPE.CLIPPED);

  return Boolean(hasClippedVideo);
};

export default function formatEvent(event: IResponseEvent, options?: { leagueId?: string; isUS?: boolean; shouldSwapTeams?: boolean }): IEvent {
  const sportType = event.attributes.gameInfo.name;
  const gameInfo = formatEventGameInfo(event.attributes.gameInfo, { isUS: options?.isUS, shouldSwapTeams: options?.shouldSwapTeams });
  const isAvailableEventLineup = (isFeatureEnabled("enable_event_add_lineup") && event.attributes?.isLineupEnabled) ?? false;
  const canEdit = (event.attributes.status === EVENT_STATUS.VOD && event.meta.permissions?.includes("write")) || false;

  const data: IEvent = {
    id: event.id,
    eventId: event.id,
    eventAbeId: event.attributes.external?.abe,
    favoriteId: event.relationships?.favorite?.id,
    type: "event",
    source_type: "video",
    title: event.attributes.name,
    created_at: event.meta.createdAt,
    start_date: event.attributes.startDateTime,
    season: event.attributes.season,
    status: event.attributes.status === "upcoming_live" ? "upcoming" : event.attributes.status,
    sport_type: sportType,
    views_count: event.attributes.statistics?.viewCount || 0,
    video_duration: event.attributes.duration,
    teamId: event.attributes.clubId,
    leagueId: options?.leagueId,
    tenantId: event.attributes.tenantId,
    poster: event.attributes.videosList?.find((v) => v.thumbnail)?.thumbnail || event.attributes.thumbnails?.hd || "",
    angles: formatEventVideoAngles(event),
    game_info: gameInfo,
    breakdowns: {
      type: event.attributes.breakdowns?.type || null,
      status: event.attributes.breakdowns?.status || null,
    },
    logging: event.attributes.logging,
    permissions: {
      canExportStatsPDF: isFeatureEnabled("enable_event_stats_export_to_pdf") && ALLOWED_SPORT_TYPES_FOR_PDF_EXPORT.includes(sportType),
      canEdit,
      canExport: event.meta.permissions?.includes("export") || false,
      canAddToFavorites: event.meta.permissions?.includes("favorite") || false,
      canDownloadVideoWithoutLogin: isFeatureEnabled("enable_event_download_video_without_login") || false,
      canOpenVS: (Boolean(event.relationships?.club?.attributes?.external?.vidswap) && event.meta.permissions?.includes("deeplink:overview")) || false,
      canOpenChartsVS: (Boolean(event.relationships?.club?.attributes?.external?.vidswap) && event.meta.permissions?.includes("deeplink:chart")) || false,
      canOpenCharts: event.meta.permissions?.includes("stats:charts") || false,
      canOpenEditorVS: (Boolean(event.relationships?.club?.attributes?.external?.vidswap) && event.meta.permissions?.includes("deeplink:editor")) || false,
      canEditOnVS:
        (Boolean(
          (isFeatureEnabled("enable_event_add_lineup") ? !event.attributes?.isLineupEnabled : true) && event.relationships?.club?.attributes?.external?.vidswap
        ) &&
          event.meta.permissions?.includes("deeplink:lineup")) ||
        false,
      canSendForBreakdown: event.meta.permissions?.includes("write") || false,
      canEditTag: event.meta.permissions?.includes("tag:write") || false,
      canDeleteTag: event.meta.permissions?.includes("tag:delete") || false,
      canDelete: event.meta.permissions?.includes("delete") || false,
      canShare: event.meta.permissions?.includes("share") || false,
      canShareDownloadableFiles: (event.meta.permissions?.includes("share") && isFeatureEnabled("enable_event_share_downloadable_files")) || false,
      canEditLineup: isAvailableEventLineup && getEditLineupPermission(event.meta.permissions),
      canEditDetails: canEdit && getEditEventDetails(gameInfo),
      canSaveSharedExternal: event.meta.permissions?.includes("club:save") || false,
      availableStats: event.meta.permissions?.includes("stats:overview") || false,
      availableLeaders: isFeatureEnabled("enable_event_leaders") || false,
      availableAthletesStats: isFeatureEnabled("enable_event_athlete_stats_tab") || false,
      availableTeamStats: isFeatureEnabled("enable_event_team_stats_tab") || false,
      availableAthleteStats: isFeatureEnabled("enable_event_athlete_stats_tab") || false,
      availableEventLineup: isAvailableEventLineup,
      useV2PlaylistsAPI: shouldUseV2PlaylistsAPI(event),
    },
    is_favorite: Boolean(event.relationships?.favorite),
    external: {
      abe: event.attributes.external?.abe,
      vidswap: event.attributes.external?.vidswap,
    },
  };

  const auto_highlight = event.attributes.highlights.find((h) => h.type === "autohighlight");

  if (auto_highlight) data.auto_highlight = formatInlineHighlight(auto_highlight, event);

  return data;
}

export function formatMBEEvent(event: IResponseMBEEvent): IMbeEvent {
  const data: IMbeEvent = {
    id: event.id,
    eventId: event.id,
    type: "mbe-event",
    source_type: "video",
    title: event.attributes.name || "",
    created_at: "", // TODO: Add when support will be added
    start_date: event.attributes.startDate as unknown as Date,
    end_date: event.attributes.endDate as unknown as Date,
    status: "vod",
    sport_type: event.attributes.sportType as TSportType,
    angles: formatMBEEventVideoAngles(event),
    views_count: 0,
    is_favorite: false,
    game_info: {
      team1_name: event.attributes.teams?.homeTeamName || "",
      // @ts-expect-error ... TODO: Update this in atlit swagger
      team1_score: event.attributes.teams?.homeScore,
      team1_logo_url: event.attributes.teams?.homeTeamLogo,
      team1_color: event.attributes.teams?.homeTeamColor,
      team1_id: event.attributes.teams?.homeTeamId,
      team2_name: event.attributes.teams?.awayTeamName || "",
      // @ts-expect-error ... TODO: Update this in atlit swagger
      team2_score: event.attributes.teams?.awayScore,
      team2_logo_url: event.attributes.teams?.awayTeamLogo,
      team2_color: (event.attributes.teams?.awayTeamColor as unknown as string) || "",
      team2_id: event.attributes.teams?.awayTeamId,
      type: event.attributes.gameType as TEventGameType,
    },
    permissions: {
      canAddToFavorites: false,
      canDownloadVideoWithoutLogin: isFeatureEnabled("enable_event_download_video_without_login") || false,
    },
  };

  return data;
}

// Helper to check if US swap should be applied for a given event type
export function shouldReverseTeamsOrder(eventType: TEventGameType, isUS: boolean): boolean {
  return isUS && (EVENT_TYPES_WITH_HOME_AWAY_TEAM as readonly string[]).includes(eventType);
}

export function formatEventGameInfo(
  info: IResponseEvent["attributes"]["gameInfo"],
  { isUS = false, shouldSwapTeams = true }: { isUS?: boolean; shouldSwapTeams?: boolean }
): IEvent["game_info"] {
  const shouldSwapSides = shouldSwapTeams && shouldReverseTeamsOrder(info.type, isUS) && Boolean(info.team1Id && info.team2Id);

  return {
    team1_name: shouldSwapSides ? info.team2 : info.team1,
    team1_score: shouldSwapSides ? info.score2 : info.score1,
    team1_logo_url: shouldSwapSides ? info.team2Logo : info.team1Logo,
    team1_color: shouldSwapSides ? info.team2Color : info.team1Color,
    team1_id: shouldSwapSides ? info.team2Id : info.team1Id,
    team1_is_official: info?.[shouldSwapSides ? "awayTeam" : "homeTeam"]?.attributes?.isOfficialTeam ?? true,
    team2_name: shouldSwapSides ? info.team1 : info.team2,
    team2_score: shouldSwapSides ? info.score1 : info.score2,
    team2_logo_url: shouldSwapSides ? info.team1Logo : info.team2Logo,
    team2_color: shouldSwapSides ? info.team1Color : info.team2Color,
    team2_id: shouldSwapSides ? info.team1Id : info.team2Id,
    team2_is_official: info?.[shouldSwapSides ? "homeTeam" : "awayTeam"]?.attributes?.isOfficialTeam ?? true,
    type: info.type,
    lineup: info.lineup?.map((l) => l.attributes) || [],
    homeTeam: info.homeTeam,
    awayTeam: info.awayTeam,
  };
}

export function formatTag(tag: IResponseTag): ITag {
  return {
    id: tag.id,
    title: tag.attributes.name || capitalize(tag.attributes.type),
    type: tag.attributes.type,
    playerNumber: tag.attributes.player === null ? undefined : tag.attributes.player,
    startTime: tag.attributes.startTime,
    endTime: tag.attributes.endTime,
  };
}

export function formatClip(data: IResponseClip): IClip {
  return {
    id: data.id,
    ownerId: data.attributes.userId,
    eventId: data.attributes.eventId,
    favoriteId: data.relationships?.favorite?.id,
    type: "clip",
    source_type: "video",
    title: data.attributes.name,
    created_at: data.meta.createdAt,
    views_count: data.attributes.statistics.viewCount || 0,
    isPending: data.attributes.status === "creating" || !data.attributes.urls.clip,
    status: data.attributes.status,
    poster: data.attributes.urls.thumbnail,
    angles: formatClipVideoAngles(data),
    is_favorite: Boolean(data.relationships?.favorite),
    sport_type: data.relationships?.sportType,
    permissions: {
      canDelete: data.meta.permissions?.includes("delete") || false,
      canAddToFavorites: data.meta.permissions?.includes("favorite") || false,
      canDownloadVideoWithoutLogin: isFeatureEnabled("enable_event_download_video_without_login") || false,
    },
  };
}

export function formatInlineHighlight(data: IResponseEvent["attributes"]["highlights"][0], event: IResponseEvent): IGameHighlight {
  return {
    id: data.id,
    type: data.type,
    source_type: "video",
    eventId: event.id,
    favoriteId: data.relationships?.favorite?.id,
    title: data.name || (data.type === "autohighlight" ? "Game recap" : data.type),
    created_at: event.attributes.endDateTime,
    views_count: data.statistics.viewCount || 0,
    poster: data.thumbnail,
    angles: formatInlineHighlightVideoAngles(data),
    is_favorite: Boolean(data.relationships?.favorite),
    sport_type: event.attributes.gameInfo.name,
    permissions: {
      canDelete: data.meta?.permissions?.includes("delete") || false,
      canAddToFavorites: data.meta?.permissions?.includes("favorite") || false,
      canDownloadVideoWithoutLogin: isFeatureEnabled("enable_event_download_video_without_login") || false,
    },
  };
}

export function formatHighlight(data: IResponseHighlight): IGameHighlight {
  return {
    id: data.id,
    type: data.attributes.type || data.type,
    source_type: "video",
    eventId: data.attributes.eventId,
    favoriteId: data.relationships?.favorite?.id,
    title: data.attributes.name || "Game recap",
    created_at: data.meta.createdAt,
    views_count: data.attributes.statistics.viewCount || 0,
    poster: data.attributes.urls.thumbnail,
    angles: formatHighlightVideoAngles(data),
    is_favorite: Boolean(data.relationships?.favorite),
    sport_type: data.attributes.sportType,
    permissions: {
      canDelete: data.meta.permissions?.includes("delete") || false,
      canAddToFavorites: data.meta.permissions?.includes("favorite") || false,
      canDownloadVideoWithoutLogin: isFeatureEnabled("enable_event_download_video_without_login") || false,
    },
  };
}
export function formatAthleteHighlight(data: IResponsePlayerHighlight): IAthleteHighlight {
  return {
    id: data.id,
    type: "player-highlight",
    source_type: "video",
    eventId: data.attributes.eventId,
    favoriteId: data.relationships?.favorite?.id,
    title: `#${data.attributes.team.player}`,
    teamColor: data.attributes.team.color,
    playerNumber: data.attributes.team.player,
    created_at: data.meta.createdAt,
    views_count: data.attributes.statistics.viewCount || 0,
    poster: data.attributes.urls.thumbnail,
    angles: formatPlayerHighlightVideoAngles(data),
    is_favorite: Boolean(data.relationships?.favorite),
    sport_type: data.relationships?.sportType,
    permissions: {
      canDelete: data.meta.permissions?.includes("delete") || false,
      canAddToFavorites: data.meta.permissions?.includes("favorite") || false,
      canDownloadVideoWithoutLogin: isFeatureEnabled("enable_event_download_video_without_login") || false,
    },
  };
}

export function formatFavoriteVideo(item: IResponseFavoriteVideo): IBasicVideo | IPlaylist {
  const favoriteData = { favoriteId: item.relationships?.favorite?.id, is_favorite: true };

  if (item.type === VIDEO_TYPE.EVENT) {
    return Object.assign({}, formatEvent(item as IResponseEvent), favoriteData);
  } else if (item.type === VIDEO_TYPE.CLIP) {
    return Object.assign({}, formatClip(item as IResponseClip), favoriteData);
  } else if (item.type === VIDEO_TYPE.GAME_HIGHLIGHT) {
    return Object.assign({}, formatHighlight(item as IResponseHighlight), favoriteData);
  } else if (item.type === VIDEO_TYPE.ATHLETE_HIGHLIGHT) {
    return Object.assign({}, formatAthleteHighlight(item as IResponsePlayerHighlight), favoriteData);
  } else if (item.type === VIDEO_TYPE.PLAYLIST) {
    return Object.assign({}, formatPlaylist(item as IResponsePlaylist), favoriteData);
  }

  // TODO: Handle additional cases
  return Object.assign({}, formatClip(item as IResponseClip), favoriteData);
}

export function formatScoreStats(data: IResponseEventStatsScores, options?: { isUS?: boolean }): IEventScoreStats {
  const { homeTeam, awayTeam, stats: statsData } = data.data?.attributes ?? {};

  if (!statsData) throw new Error("Invalid score stats data format");
  const stats = statsData.map((stat) => formatScoreStat(stat, options?.isUS));

  return { ...getTeamEntities(homeTeam, awayTeam, options?.isUS), stats };
}

export function formatGameStats(data: IResponseEventStatsTeams, options?: { isUS?: boolean }): IEventGameStats {
  const { homeTeam, awayTeam, stats: statsData } = data.data?.attributes ?? {};

  if (!statsData) throw new Error("Invalid game stats data format");
  const stats = statsData.map((stat) => formatGameStat(stat, options?.isUS));

  return { ...getTeamEntities(homeTeam, awayTeam, options?.isUS), stats };
}

export function formatGameStatsV3(data: IResponseEventStatsTeams, options?: { isUS?: boolean }): IEventGameStats {
  const { homeTeam, awayTeam, stats: statsData } = data.data?.attributes ?? {};

  if (!statsData) throw new Error("Invalid game stats V3 data format");
  const stats = statsData.map((stat) => formatGameStatV3(stat, options?.isUS));

  return { ...getTeamEntities(homeTeam, awayTeam, options?.isUS), stats };
}

type IEventMetricStatValue = { value: string; format?: IStatsRepresentation; url?: string };

export function getPlaylistForStat(stat: ItemStatValue): string | undefined {
  const { playlistId, displayValue } = stat as { playlistId?: string; displayValue: string };

  if (!playlistId || displayValue === "0") {
    return undefined;
  }
  return playlistId;
}

export function formatAthleteStats(data: IResponseEventStatsRosters, options?: { isUS?: boolean }): IEventAthleteStats {
  const { homeTeam, awayTeam, stats: statsData } = data.data?.attributes ?? {};

  if (!statsData) throw new Error("Invalid athlete stats data format");
  const stats = statsData.map(({ athlete, metrics }) => ({
    metrics: metrics.map((metric) => {
      const statValue = getStatValue(metric.value, { representation: metric.representation, playlistId: metric.playlistId });
      return {
        ...metric,
        value: { text: statValue.displayValue, number: "numericValue" in statValue ? statValue.numericValue : Number(statValue.displayValue) },
        playlistId: getPlaylistForStat(statValue),
      };
    }),
    athlete: { ...athlete, team: getTeamSide({ team: athlete.team, isUS: options?.isUS }) },
  }));

  return { ...getTeamEntities(homeTeam, awayTeam, options?.isUS), stats };
}

export function formatAthleteStatsV3(data: IResponseEventStatsRostersV3, options?: { isUS?: boolean }): IEventAthleteStatsV3 {
  const { homeTeam = {}, awayTeam = {}, stats: statsData } = data.data?.attributes ?? {};

  if (!statsData?.tables || !statsData?.metadata?.athletes) throw new Error("Invalid athlete stats V3 data format");

  const athletesById = new Map(statsData.metadata.athletes.map((a) => [a._id, a]));
  const stats: Record<string, IEventAthleteMetricStats[]> = {};

  for (const { columns, data } of Object.values(statsData.tables)) {
    const column = Array.isArray(columns) ? columns[0] : columns;

    if (!column?.name || !data?.length) continue;

    stats[column.name] = data
      .map((d) => {
        const athlete = athletesById.get(d.athlete);
        if (!athlete) return null;

        const { _id, name, team, number, position } = athlete;

        const metrics = column.children
          ?.map((c) => {
            if (!c?.columnId) return null;

            const metricValue = d[c.columnId as keyof typeof d] as IEventMetricStatValue;

            const statValue = getStatValue(metricValue.value, { representation: metricValue.format, playlistId: metricValue.url });

            return {
              name: c.name,
              representation: metricValue.format,
              shortName: c.shortName,
              playlistId: getPlaylistForStat(statValue),
              value: { text: statValue.displayValue, number: "numericValue" in statValue ? statValue.numericValue : Number(statValue.displayValue) },
            };
          })
          .filter(Boolean);

        if (!metrics?.length) return null;

        return {
          athlete: { id: _id as number | string, team, name, number, position },
          metrics,
        };
      })
      .filter(Boolean) as IEventAthleteMetricStats[];
  }

  return { ...getTeamEntities(homeTeam, awayTeam, options?.isUS), stats };
}

function formatScoreStat(
  { name, shortName, homeTeamValue, awayTeamValue }: IResponseEventStatsScores["data"]["attributes"]["stats"][0],
  isUS?: boolean
): IEventScoreStats["stats"][0] {
  return {
    name,
    shortName,
    homeTeamValue: isUS ? awayTeamValue : homeTeamValue,
    awayTeamValue: isUS ? homeTeamValue : awayTeamValue,
  };
}

// Generic function to handle stats formatting
function formatGameStatBase(
  {
    name,
    shortName,
    homeTeamValue,
    awayTeamValue,
    category,
    homeTeamUrl,
    awayTeamUrl,
    homePlaylistId,
    awayPlaylistId,
    maxValue,
  }: IResponseEventStatsTeams["data"]["attributes"]["stats"][0],
  getValueFn: (value: any, options?: IStatValueOptions) => any,
  isUS: boolean
): IGameStats {
  let representation: "percent" | "time" | undefined;

  if (shortName.includes("%") || name.toLowerCase().includes("percentage")) {
    representation = "percent";
  } else if (shortName.includes(":") || name.toLowerCase().includes("time")) {
    representation = "time";
  }

  const team1Value = getValueFn(homeTeamValue, { representation, playlistId: homeTeamValue.playlistId || homePlaylistId });
  const team2Value = getValueFn(awayTeamValue, { representation, playlistId: awayTeamValue.playlistId || awayPlaylistId });

  const calculatedMaxValue = Math.max(Number(team1Value.numericValue), Number(team2Value.numericValue)) || 100;

  return {
    name,
    shortName,
    homeTeamValue: isUS ? team2Value : team1Value,
    awayTeamValue: isUS ? team1Value : team2Value,
    category,
    homeTeamUrl: isUS ? awayTeamUrl : homeTeamUrl,
    awayTeamUrl: isUS ? homeTeamUrl : awayTeamUrl,
    homePlaylistId: isUS ? awayPlaylistId : homePlaylistId,
    awayPlaylistId: isUS ? homePlaylistId : awayPlaylistId,
    maxValue: representation === "percent" ? 100 : (maxValue ?? calculatedMaxValue),
  };
}

function formatGameStat(statItem: IResponseEventStatsTeams["data"]["attributes"]["stats"][0], isUS?: boolean): IGameStats {
  return formatGameStatBase(statItem, getStatValue, isUS ?? false);
}

function formatGameStatV3(statItem: IResponseEventStatsTeams["data"]["attributes"]["stats"][0], isUS?: boolean): IGameStats {
  return formatGameStatBase(statItem, getStatValueV3, isUS ?? false);
}

export function formatSharedExternalEvent(event: IResponseSharedExternalEvent): ISharedExternalEvent {
  return {
    id: event.id,
    clubId: event.attributes.clubId,
    userId: event.attributes.userId,
    eventId: event.attributes.id,
    type: event.type,
    scope: event.attributes.scope,
  };
}
